import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listEvents, joinEvent } from "@/lib/events";
import { mapboxgl, MAP_STYLE, MAPBOX_TOKEN } from "@/lib/mapbox";
import { useAuth } from "@/context/AuthContext";
import { Crosshair, X, Calendar, MapPin, ArrowRight, Check, Clock, Plus } from "lucide-react";
import { toast } from "sonner";
import { fmtError } from "@/lib/supabase";
import Avatar from "@/components/Avatar";
import { coverFor } from "@/lib/defaultEventImages";
import { CATEGORY_EMOJI } from "@/lib/categories";

const FALLBACK_CENTER = [78.9629, 20.5937];
const FALLBACK_ZOOM = 3.6;

const CATEGORY_ICON = CATEGORY_EMOJI;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function MapPage() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;

    // Clear the old (buggy) geocode cache from previous builds so users get
    // a clean slate.
    try { localStorage.removeItem("inonit:cityCache:v1"); } catch { /* noop */ }

    listEvents().then((data) => {
      if (!alive) return;
      const withCoords = (data || []).filter(
        (e) => e.latitude != null && e.longitude != null
      );

      // Deterministic spread for events sharing the same exact lat/lng so they
      // don't stack pixel-perfect. Same event id → same offset every visit.
      const groups = new Map();
      for (const e of withCoords) {
        const key = `${e.latitude.toFixed(4)}|${e.longitude.toFixed(4)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(e);
      }
      const spread = [];
      for (const [, group] of groups) {
        if (group.length === 1) { spread.push(group[0]); continue; }
        // Arrange overlapping pins in a tiny ring (~80m radius).
        const R = 0.0008;
        group.forEach((e, i) => {
          const t = (i / group.length) * Math.PI * 2;
          spread.push({
            ...e,
            latitude:  e.latitude  + Math.sin(t) * R,
            longitude: e.longitude + Math.cos(t) * R,
          });
        });
      }
      setEvents(spread);
      setLoading(false);
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!containerRef.current || !MAPBOX_TOKEN || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: FALLBACK_CENTER,
      zoom: FALLBACK_ZOOM,
      projection: "mercator",
      attributionControl: false,
      dragRotate: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    mapRef.current = map;

    // 🔧 Mapbox bakes the canvas size at construction time. If the container
    // has CSS layout still settling (Tailwind arbitrary h-calc values resolving,
    // fonts loading, etc.) the canvas can end up too small and stays that way
    // until something triggers a resize. Defensive resizes + a ResizeObserver
    // guarantee the canvas always matches the container's true pixel size.
    const safeResize = () => { try { map.resize(); } catch { /* noop */ } };
    requestAnimationFrame(safeResize);
    const t1 = setTimeout(safeResize, 100);
    const t2 = setTimeout(safeResize, 500);
    map.on("style.load", safeResize);
    map.on("load", safeResize);

    const ro = new ResizeObserver(safeResize);
    ro.observe(containerRef.current);
    window.addEventListener("resize", safeResize);

    // Cleanup timers/observers when the map unmounts.
    const cleanupResize = () => {
      clearTimeout(t1);
      clearTimeout(t2);
      try { ro.disconnect(); } catch { /* noop */ }
      window.removeEventListener("resize", safeResize);
    };

    map.on("load", () => {

      // GeoJSON source with native clustering.
      map.addSource("events", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 50,
      });

      // Chunky lime cluster bubble + count badge.
      map.addLayer({
        id: "cluster-bg",
        type: "circle",
        source: "events",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#D7FF2F",
          "circle-stroke-color": "#0A0A0A",
          "circle-stroke-width": 3,
          "circle-radius": ["step", ["get", "point_count"], 20, 5, 26, 20, 34, 50, 42],
        },
      });
      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "events",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": 14,
          "text-allow-overlap": true,
        },
        paint: { "text-color": "#0A0A0A" },
      });

      // Click a cluster → expand it.
      map.on("click", "cluster-bg", (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ["cluster-bg"] });
        const clusterId = features[0]?.properties?.cluster_id;
        if (clusterId == null) return;
        const src = map.getSource("events");
        src.getClusterExpansionZoom(clusterId, (err, z) => {
          if (err) return;
          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: z,
            duration: 600,
          });
        });
      });
      map.on("mouseenter", "cluster-bg", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "cluster-bg", () => { map.getCanvas().style.cursor = ""; });

      setReady(true);
    });
    return () => { cleanupResize(); try { map.remove(); } catch { /* noop */ } mapRef.current = null; };
  }, []);

  // Mark when the map has finished loading the style + source.
  const [ready, setReady] = useState(false);

  // Push events into the GeoJSON source.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const src = map.getSource("events");
    if (!src) return;
    src.setData({
      type: "FeatureCollection",
      features: events.map((e) => ({
        type: "Feature",
        properties: {
          id: e.id,
          state: user?.id && e.host_id === user.id
            ? "mine"
            : (e.joined ? "joined" : "open"),
        },
        geometry: { type: "Point", coordinates: [e.longitude, e.latitude] },
      })),
    });

    // Fit bounds on first data load.
    if (events.length === 1) {
      map.flyTo({ center: [events[0].longitude, events[0].latitude], zoom: 12, duration: 700 });
    } else if (events.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      events.forEach((e) => bounds.extend([e.longitude, e.latitude]));
      try { map.fitBounds(bounds, { padding: 100, duration: 700, maxZoom: 12 }); } catch { /* noop */ }
    }
  }, [events, ready, user?.id]);

  // Render DOM markers for UNCLUSTERED points only — preserves the chunky
  // pin design while letting Mapbox handle aggregation.
  const eventByIdRef = useRef(new Map());
  useEffect(() => {
    eventByIdRef.current = new Map(events.map((e) => [e.id, e]));
  }, [events]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const refresh = () => {
      const src = map.getSource("events");
      if (!src) return;
      const features = map.querySourceFeatures("events", {
        filter: ["!", ["has", "point_count"]],
      });
      const seen = new Set();
      const wanted = new Map();
      for (const f of features) {
        const id = f.properties?.id;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        wanted.set(id, f);
      }

      markersRef.current = markersRef.current.filter((entry) => {
        if (wanted.has(entry.eventId)) return true;
        try { entry.marker.remove(); } catch { /* noop */ }
        return false;
      });
      const existingIds = new Set(markersRef.current.map((m) => m.eventId));

      for (const [id, f] of wanted) {
        if (existingIds.has(id)) continue;
        const e = eventByIdRef.current.get(id);
        if (!e) continue;
        const el = document.createElement("button");
        el.className = "map-pin";
        el.dataset.state = f.properties?.state || "open";
        el.setAttribute("aria-label", e.title);
        el.innerHTML = `<span class="pin-dot"></span>`;
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          setSelected(e);
          try {
            map.flyTo({
              center: [e.longitude, e.latitude],
              zoom: Math.max(map.getZoom(), 13),
              duration: 600,
              essential: true,
              padding: { bottom: 280 },
            });
          } catch { /* noop */ }
        });
        const marker = new mapboxgl.Marker({ element: el, anchor: "bottom" })
          .setLngLat(f.geometry.coordinates)
          .addTo(map);
        markersRef.current.push({ marker, el, eventId: id });
      }
    };

    const onMapClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ["cluster-bg"] });
      if (features.length === 0) setSelected(null);
    };

    map.on("moveend", refresh);
    map.on("sourcedata", (ev) => {
      if (ev.sourceId === "events" && ev.isSourceLoaded) refresh();
    });
    map.on("click", onMapClick);

    refresh();

    return () => {
      try {
        map.off("moveend", refresh);
        map.off("click", onMapClick);
      } catch { /* noop */ }
    };
  }, [ready]);

  // Highlight active pin
  useEffect(() => {
    markersRef.current.forEach(({ el, eventId }) => {
      if (!el) return;
      if (selected?.id === eventId) el.classList.add("is-active");
      else el.classList.remove("is-active");
    });
  }, [selected]);

  const meMarkerRef = useRef(null);

  const locateMe = () => {
    if (!navigator.geolocation || !mapRef.current) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lngLat = [pos.coords.longitude, pos.coords.latitude];
        mapRef.current.flyTo({
          center: lngLat,
          zoom: 12,
          duration: 800,
          essential: true,
        });
        // Drop / move the "you are here" marker.
        if (!meMarkerRef.current) {
          const el = document.createElement("div");
          el.className = "me-pin";
          el.innerHTML = `<span class="ring"></span><span class="dot"></span>`;
          meMarkerRef.current = new mapboxgl.Marker({ element: el })
            .setLngLat(lngLat)
            .addTo(mapRef.current);
        } else {
          meMarkerRef.current.setLngLat(lngLat);
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 6000 }
    );
  };

  return (
    <div className="relative h-[calc(100vh-5rem-6.5rem)] md:h-[calc(100vh-5rem)]">
      <div ref={containerRef} data-testid="map-page-canvas" className="w-full h-full" />

      {/* Top-left chip */}
      <div className="absolute top-4 left-4 right-4 flex flex-col gap-2 pointer-events-none">
        <div className="pointer-events-auto inline-flex self-start items-center gap-2 bg-ink text-paper rounded-full px-4 py-2 border-[2.5px] border-ink shadow-pop">
          <span className="font-bold text-sm">
            {loading ? "Loading…" : `${events.length} on the map`}
          </span>
        </div>
        <button
          type="button"
          onClick={locateMe}
          data-testid="map-locate-me"
          className="pointer-events-auto self-start inline-flex items-center gap-2 bg-paper border-[2.5px] border-ink rounded-full px-4 py-2 text-sm font-bold shadow-pop active:translate-x-[2px] active:translate-y-[2px] active:shadow-pop-active transition-[transform,box-shadow]"
        >
          <Crosshair size={14} strokeWidth={2.2} /> Use my location
        </button>
      </div>

      {/* Empty state when no events have map coordinates */}
      {!loading && events.length === 0 && (
        <div
          data-testid="map-empty-state"
          className="absolute left-4 right-4 top-1/2 -translate-y-1/2 z-20 flex justify-center pointer-events-none"
        >
          <div className="pointer-events-auto pop bg-paper p-6 sm:p-8 max-w-sm text-center">
            <div className="w-14 h-14 mx-auto mb-4 border-[2.5px] border-ink rounded-full bg-lime flex items-center justify-center">
              <MapPin size={22} strokeWidth={2.2} />
            </div>
            <h2 className="font-display text-2xl leading-tight mb-2">
              Nothing pinned yet.
            </h2>
            <p className="text-ink/65 text-sm leading-relaxed mb-5">
              Events show up here only when the host pins an exact spot. Be the first.
            </p>
            <Link to={user ? "/create" : "/login?next=/create"} className="cta-primary">
              <Plus size={14} strokeWidth={2.8} /> Post & pin an event
            </Link>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 left-4 pointer-events-none">
        <div className="pop-sm bg-paper p-3 pr-4 text-xs">
          <p className="font-bold uppercase tracking-widest text-ink/70 mb-1.5">Legend</p>
          <div className="flex flex-col gap-1">
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-lime border-2 border-ink" /> You're going</span>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-paper border-2 border-ink" /> Not joined</span>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-ink border-2 border-ink" /> Hosting</span>
          </div>
        </div>
      </div>

      {!MAPBOX_TOKEN && (
        <div className="absolute inset-0 flex items-center justify-center text-ink/55 text-sm uppercase tracking-widest">
          Map unavailable — token missing.
        </div>
      )}

      {/* Bottom sheet — tap a pin to preview the event */}
      <MapEventSheet
        event={selected}
        onClose={() => setSelected(null)}
        onRsvp={async (updated) => {
          // Refresh local events list + bubble-up the joined/pending state
          // for instant button update, then close the sheet.
          setEvents((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));
          setSelected(updated);
        }}
      />

      <style>{`
        .map-pin {
          width: 28px; height: 36px;
          cursor: pointer;
          position: relative;
          /* IMPORTANT: do NOT set transform/animation on this element.
             Mapbox uses transform: translate3d(...) on the marker root to
             position it; any local transform would clobber that. We animate
             the inner .pin-dot instead. */
        }
        .map-pin .pin-dot {
          position: absolute; left: 50%; top: 50%;
          width: 22px; height: 22px;
          margin-left: -11px; margin-top: -11px;
          border-radius: 9999px;
          border: 2.5px solid #0A0A0A;
          background: #FCFAF1;
          box-shadow: 0 3px 0 0 #0A0A0A;
          transition: transform 200ms cubic-bezier(0.34,1.56,0.64,1);
          animation: map-pin-drop 520ms cubic-bezier(0.34,1.56,0.64,1) both;
        }
        .map-pin[data-state="joined"] .pin-dot { background: #D7FF2F; }
        .map-pin[data-state="mine"] .pin-dot   { background: #0A0A0A; }
        .map-pin:hover .pin-dot { transform: translateY(-4px) scale(1.12); }
        .map-pin.is-active .pin-dot {
          transform: translateY(-6px) scale(1.18);
          background: #D7FF2F;
          box-shadow: 0 4px 0 0 #0A0A0A, 0 0 0 4px rgba(214,255,47,0.35);
        }
        @keyframes map-pin-drop {
          0%   { transform: translateY(-22px) scale(0.7); opacity: 0; }
          60%  { transform: translateY(2px)  scale(1.08); opacity: 1; }
          100% { transform: translateY(0)    scale(1);    opacity: 1; }
        }

        /* "You are here" pulsing dot */
        .me-pin {
          width: 22px; height: 22px;
          position: relative;
          pointer-events: none;
        }
        .me-pin .dot {
          position: absolute; inset: 0; margin: auto;
          width: 14px; height: 14px;
          border-radius: 9999px;
          background: #2962ff;
          border: 2.5px solid #fff;
          box-shadow: 0 0 0 2px rgba(41,98,255,0.35), 0 2px 6px rgba(0,0,0,0.25);
          z-index: 2;
        }
        .me-pin .ring {
          position: absolute; inset: 0; margin: auto;
          width: 14px; height: 14px;
          border-radius: 9999px;
          background: rgba(41,98,255,0.55);
          animation: me-pulse 2.2s cubic-bezier(0.22,0.61,0.36,1) infinite;
          z-index: 1;
        }
        @keyframes me-pulse {
          0%   { transform: scale(1);   opacity: 0.55; }
          80%  { transform: scale(3);   opacity: 0; }
          100% { transform: scale(3);   opacity: 0; }
        }

        /* Editorial controls — cream + ink chunky group, large tap targets */
        .mapboxgl-ctrl-group {
          background: #FCFAF1 !important;
          box-shadow: 3px 3px 0 0 #0A0A0A !important;
          border: 2.5px solid #0A0A0A !important;
          border-radius: 1rem !important;
          overflow: hidden;
        }
        .mapboxgl-ctrl-group button {
          background-color: transparent !important;
          width: 42px !important;
          height: 42px !important;
        }
        .mapboxgl-ctrl-group button + button { border-top: 2px solid #0A0A0A !important; }
        .mapboxgl-ctrl-group button:hover { background-color: #D7FF2F !important; }
        .mapboxgl-ctrl-attrib {
          background: rgba(244,240,225,0.85) !important;
          color: rgba(10,10,10,0.55) !important;
          border-radius: 0.5rem;
        }
        .mapboxgl-ctrl-attrib a { color: #0A0A0A !important; }
      `}</style>
    </div>
  );
}

function MapEventSheet({ event, onClose, onRsvp }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  // Render after mount for the slide-up animation.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (event) {
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }
    setMounted(false);
  }, [event?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!event) return null;

  const status = event.joined ? "approved" : event.pending ? "pending" : null;
  const atCapacity = event.capacity != null && event.attendees_count >= event.capacity;
  const isHost = user?.id && user.id === event.host_id;

  let chipLabel = "JOIN";
  let chipKlass = "bg-lime text-ink";
  if (isHost) { chipLabel = "HOSTING"; chipKlass = "bg-ink text-paper"; }
  else if (status === "approved") { chipLabel = "IN"; chipKlass = "bg-lime text-ink"; }
  else if (status === "pending") { chipLabel = "PENDING"; chipKlass = "bg-paper text-ink"; }
  else if (atCapacity) { chipLabel = "FULL"; chipKlass = "bg-paper text-ink/55"; }
  else if (event.requires_approval) { chipLabel = "REQUEST"; chipKlass = "bg-lime text-ink"; }
  if (busy) chipLabel = "…";

  const chipIcon = busy
    ? null
    : status === "approved" ? <Check size={11} strokeWidth={3} />
    : status === "pending"  ? <Clock size={11} strokeWidth={2.5} />
    : null;

  const disabled = busy || isHost || status === "approved" || status === "pending" || atCapacity;

  const onAction = async () => {
    if (disabled) return;
    if (!user) {
      navigate(`/login?next=/map`);
      return;
    }
    setBusy(true);
    try {
      const newStatus = await joinEvent(event.id, user.id, !!event.requires_approval);
      const merged = {
        ...event,
        joined: newStatus === "approved",
        pending: newStatus === "pending",
        attendees_count: newStatus === "approved"
          ? (event.attendees_count || 0) + 1
          : event.attendees_count || 0,
      };
      toast.success(newStatus === "pending" ? "Request sent. The host will decide." : "You're in. See you there.");
      onRsvp?.(merged);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="map-event-sheet"
      role="dialog"
      aria-label={`Preview: ${event.title}`}
      className={`absolute left-3 right-3 bottom-3 z-30 transition-transform duration-300 ease-out
                  ${mounted ? "translate-y-0" : "translate-y-[120%]"}`}
    >
      <div className="pop p-0 overflow-hidden bg-paper">
        {/* drag handle */}
        <div className="flex justify-center pt-2 pb-1">
          <span className="w-10 h-1.5 rounded-full bg-ink/15" />
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="map-sheet-close"
          aria-label="Close preview"
          className="absolute top-2 right-2 w-9 h-9 inline-flex items-center justify-center rounded-full border-[2.5px] border-ink bg-paper hover:bg-lime transition-colors"
        >
          <X size={14} strokeWidth={2.5} />
        </button>

        <div className="flex gap-4 px-4 pb-4 pt-1">
          {/* Cover */}
          <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-2xl overflow-hidden border-[2.5px] border-ink bg-[#EDE7D2]">
            <img src={coverFor(event)} alt={event.title} className="w-full h-full object-cover" loading="lazy" />
          </div>

          {/* Body */}
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <span className="inline-flex self-start items-center gap-1 bg-paper border-[2.5px] border-ink rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-widest">
              <span aria-hidden>{CATEGORY_ICON[event.category] || "✦"}</span>
              {event.category}
            </span>
            <h3 className="font-display text-xl leading-tight tracking-tight text-ink line-clamp-2">
              {event.title}
            </h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-ink/65">
              <span className="inline-flex items-center gap-1">
                <Calendar size={11} strokeWidth={2} className="text-ink/55" />
                {formatDate(event.date_time)}
              </span>
              <span className="inline-flex items-center gap-1 truncate min-w-0">
                <MapPin size={11} strokeWidth={2} className="text-ink/55" />
                <span className="truncate">{event.rough_area || event.city}</span>
              </span>
            </div>
            {event.host_name && (
              <div className="flex items-center gap-2 mt-1">
                <Avatar src={event.host_avatar_url} name={event.host_name} size="xs" />
                <span className="text-[12px] text-ink/65 truncate">
                  by <span className="text-ink font-bold">{event.host_username ? `@${event.host_username}` : event.host_name}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer CTAs */}
        <div className="flex items-stretch border-t-[2.5px] border-ink">
          <button
            type="button"
            data-testid="map-sheet-open-event"
            onClick={() => navigate(`/events/${event.id}`)}
            className="flex-1 inline-flex items-center justify-center gap-2 py-3 text-xs font-extrabold uppercase tracking-widest text-ink hover:bg-ink/5 transition-colors"
          >
            See full details <ArrowRight size={13} strokeWidth={2.8} />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={onAction}
            data-testid="map-sheet-rsvp"
            className={`shrink-0 inline-flex items-center gap-1.5 px-5 py-3 text-xs font-extrabold uppercase tracking-widest border-l-[2.5px] border-ink ${chipKlass} ${disabled ? "cursor-not-allowed opacity-90" : "hover:brightness-95 active:brightness-90"}`}
          >
            {chipIcon} {chipLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

